import React, { useCallback, useEffect, useMemo } from "react";
import { MainSurveyStore } from "../../hooks/useMainSurvey";
import { IconButton, Switch, Typography } from "@mui/material";
import { XMarkIcon } from "shared/icons/XMarkIcon";
import { BlockForm } from "../Sections/BlockForm";
import { KababMenu } from "shared/components/Menu/KababMenu";
import { SquaresPlusIcon } from "shared/icons/SquaresPlusIcon";
import { CounterInput } from "shared/components/Inputs/CounterInput";
import { BlockFormValuesStore } from "../../hooks/useBlockFormValues";
import { BlockFieldTypes, blockToBlockValues, isBarOrPieChart, SurveyLayouts } from "../../helpers/blockForm";
import { SurveyBlocks } from "../../surveyBlocks";
import { AxisInputs } from "../FormattingInstructions/AxisInputs";
import {
  FormattingRule,
  FormattingRuleType,
  createFormattingRule,
  deleteFormattingInstructionValues,
  getFormattingRule,
  checkIfColumnHasBlocks,
  checkIfRowHasBlocks,
  DefaultMaxColumns,
  positionToSelectionArea,
} from "@coolset/cs-ui";
import { SeriesInputs } from "../FormattingInstructions/SeriesInputs";
import { TableFormattingInputs } from "../FormattingInstructions/TableFormattingInputs";
import { TableCellInputs } from "../FormattingInstructions/TableCellInputs";
import { ChartTypeHeading } from "./ChartTypeHeading";
import { PositionInstructionsStore } from "modules/MainSurveyEditor/hooks/usePositioningTable";
import { BulkBlockForm } from "../Sections/BulkBlockForm";

export const ManageDataSet = (props: ManageDataSetProps) => {
  const {
    mainSurveyStore: { subBlockToEdit, setIsTableEditOpen, forceSave, setForceSave, setIsSaving, selectedTableBlocks },
    selectedBlock,
    setSelectedBlock,
    setSelectionArea,
    blockFormValuesStore,
    positionInstructionsStore,
    onBulkDeleted,
    onBulkSaved,
  } = props;

  const { blockValues, isSaving, setBlockValues, updateBlockApi, setLastBlockValues } = blockFormValuesStore;
  const { positionInstructions, handleRepositionBlocks, handleAfterDeleteBlock, setIsAwaitingBlockUpdate } =
    positionInstructionsStore;

  const isChart = useMemo(() => blockValues.type === "chart", [blockValues]);
  const isTable = useMemo(() => blockValues.type === "table", [blockValues]);

  const isBulkMode = useMemo(
    () => isTable && (selectedTableBlocks?.length ?? 0) > 1,
    [isTable, selectedTableBlocks],
  );

  const handleFieldChange = useCallback(
    (fieldKey: string, value?: boolean | number | FormattingRule[]) => {
      console.log("fieldKey", fieldKey, "value", value);
      setBlockValues((prevBlockValues) => ({ ...prevBlockValues, [fieldKey]: value }));
    },
    [setBlockValues]
  );

  const firstRowLabelsRule = useMemo<FormattingRule | undefined>(
    () =>
      getFormattingRule(blockValues.formattingInstructions, {
        ruleType: FormattingRuleType.Row,
        position: "1",
        isLabel: true,
      }),
    [blockValues]
  );

  const partialChartPointsRule = useMemo<FormattingRule | undefined>(
    () =>
      getFormattingRule(blockValues.formattingInstructions, {
        ruleType: FormattingRuleType.Cell,
        position: "2",
        showPartialChartPoints: true,
      }),
    [blockValues]
  );

  const handleToggleFirstRowLabels = useCallback(() => {
    if (!firstRowLabelsRule) {
      const [newRules] = createFormattingRule(blockValues.formattingInstructions, {
        ruleType: FormattingRuleType.Row,
        position: "1",
        isLabel: true,
      });
      handleFieldChange(BlockFieldTypes.FormattingInstructions, newRules);
    } else {
      const [newRules] = deleteFormattingInstructionValues(blockValues.formattingInstructions, firstRowLabelsRule, [
        "isLabel",
      ]);
      handleFieldChange(BlockFieldTypes.FormattingInstructions, newRules);
    }
  }, [blockValues, firstRowLabelsRule, handleFieldChange]);

  const handleTogglePartialChartPoints = useCallback(() => {
    if (!partialChartPointsRule) {
      const [newRules] = createFormattingRule(blockValues.formattingInstructions, {
        // TODO - refactor to Table type
        ruleType: FormattingRuleType.Cell,
        position: "2",
        showPartialChartPoints: true,
      });
      handleFieldChange(BlockFieldTypes.FormattingInstructions, newRules);
    } else {
      const [newRules] = deleteFormattingInstructionValues(blockValues.formattingInstructions, partialChartPointsRule, [
        "showPartialChartPoints",
      ]);
      handleFieldChange(BlockFieldTypes.FormattingInstructions, newRules);
    }
  }, [blockValues, partialChartPointsRule, handleFieldChange]);

  const handleUpdateCanvasSize = useCallback(
    (fieldKey: BlockFieldTypes.MaxRows | BlockFieldTypes.MaxColumns, number: number) => {
      const isNotValid =
        fieldKey === BlockFieldTypes.MaxRows
          ? checkIfRowHasBlocks(number + 1, positionInstructions)
          : checkIfColumnHasBlocks(number + 1, positionInstructions);
      if (isNotValid) return;

      handleFieldChange(fieldKey, number);
    },
    [positionInstructions, handleFieldChange]
  );

  useEffect(() => {
    if (!selectedBlock) {
      return;
    }

    const values = blockToBlockValues(selectedBlock);
    setLastBlockValues(values);
    setBlockValues(values);
  }, [selectedBlock, setBlockValues, setLastBlockValues]);

  useEffect(() => {
    updateBlockApi(false, (_v, block) => (block ? setSelectedBlock(block) : null));
  }, [blockValues, setSelectedBlock, updateBlockApi]);

  useEffect(() => {
    if (!forceSave) return;

    updateBlockApi(true, (_v, block) => (block ? setSelectedBlock(block) : null));
    setForceSave(false);
  }, [forceSave, setSelectedBlock, setForceSave, updateBlockApi]);

  useEffect(() => setIsSaving(isSaving), [isSaving, setIsSaving]);

  return (
    <>
      <header className="_d-flex-ali-center-jc-sb-sm-gap">
        <Typography variant="h3">Manage Data Set</Typography>
        <IconButton onClick={() => setIsTableEditOpen(false)}>
          <XMarkIcon sx={{ width: 24, height: 24 }} />
        </IconButton>
      </header>

      <section className="_d-grid">
        <ChartTypeHeading chartDisplayType={blockValues.displayType as SurveyLayouts} />
        <div className="_d-flex-ali-center-jc-sb-sm-gap">
          <Typography variant="h4" color="text.secondary">
            Rows and columns
          </Typography>
          <KababMenu
            icon={<SquaresPlusIcon sx={{ width: 24, height: 24 }} />}
            menuProps={{ classes: { list: "_d-flex-ali-center-md-gap row-col-dialog" } }}
            items={[
              <RowColCounter
                key="rows"
                value={blockValues[BlockFieldTypes.MaxRows]}
                onChange={(num) => handleUpdateCanvasSize(BlockFieldTypes.MaxRows, num)}
                title="Rows"
              />,
              <Typography key="x" color="text.secondary" mt={2}>
                x
              </Typography>,
              <RowColCounter
                key="columns"
                value={blockValues[BlockFieldTypes.MaxColumns]}
                onChange={(num) => handleUpdateCanvasSize(BlockFieldTypes.MaxColumns, num)}
                title="Columns"
              />,
            ]}
          />
        </div>
        {isChart && (
          <>
            <div className="_d-flex-ali-center-jc-sb-sm-gap">
              <Typography variant="h4" color="text.secondary">
                First row contains axis labels
              </Typography>
              <Switch checked={!!firstRowLabelsRule} onChange={handleToggleFirstRowLabels} />
            </div>

            <div className="_d-flex-ali-center-jc-sb-sm-gap">
              <Typography variant="h4" color="text.secondary">
                Display empty numeric questions as “0”
              </Typography>
              <Switch checked={!!partialChartPointsRule} onChange={handleTogglePartialChartPoints} />
            </div>

            <AxisInputs
              formattingInstructions={blockValues.formattingInstructions ?? []}
              maxColumns={blockValues[BlockFieldTypes.MaxColumns] ?? DefaultMaxColumns}
              onChange={(rules) => handleFieldChange(BlockFieldTypes.FormattingInstructions, rules)}
              disableAxisRange={isBarOrPieChart(blockValues.displayType as SurveyLayouts)}
              disableYAxis={isBarOrPieChart(blockValues.displayType as SurveyLayouts)}
            />
          </>
        )}

        {isTable && (
          <TableFormattingInputs
            formattingInstructions={blockValues.formattingInstructions ?? []}
            onChange={(rules) => handleFieldChange(BlockFieldTypes.FormattingInstructions, rules)}
          />
        )}
      </section>

      <div className="module-divider" />

      {isChart && (
        <>
          <section>
            <SeriesInputs
              formattingInstructions={blockValues.formattingInstructions ?? []}
              onChange={(rules) => handleFieldChange(BlockFieldTypes.FormattingInstructions, rules)}
              onFocusedPosition={(position: string) => setSelectionArea(positionToSelectionArea(position))}
              onUnfocusedPosition={() => setSelectionArea("")}
              chartType={blockValues.displayType as SurveyLayouts}
            />
          </section>

          <div className="module-divider" />
        </>
      )}

      {isBulkMode ? (
        <BulkBlockForm
          mainSurveyStore={props.mainSurveyStore}
          selectedBlocks={selectedTableBlocks}
          onDeleted={onBulkDeleted}
          onBulkSaved={onBulkSaved}
        />
      ) : (
        <>
          {subBlockToEdit && isTable && (
            <TableCellInputs
              mainSurveyStore={props.mainSurveyStore}
              formattingInstructions={blockValues.formattingInstructions ?? []}
              onChange={(rules) => handleFieldChange(BlockFieldTypes.FormattingInstructions, rules)}
              positionInstructions={positionInstructions}
              onReorderBlock={handleRepositionBlocks}
              isSavingPositioning={isSaving}
              handleAfterDeleteBlock={handleAfterDeleteBlock}
              setIsAwaitingBlockUpdate={setIsAwaitingBlockUpdate}
            />
          )}

          {subBlockToEdit && (
            <BlockForm
              mainSurveyStore={props.mainSurveyStore}
              isSubBlock
              handleAfterDeleteBlock={handleAfterDeleteBlock}
              setIsAwaitingBlockUpdate={setIsAwaitingBlockUpdate}
            />
          )}
        </>
      )}
    </>
  );
};

const RowColCounter = (props: { value?: number; onChange: (num: number) => void; title: string }) => {
  const { value, onChange, title } = props;

  return (
    <div>
      <Typography color="text.secondary" mb={1}>
        {title}
      </Typography>
      <CounterInput value={value} onChange={onChange} min={1} max={1000} />
    </div>
  );
};

interface ManageDataSetProps {
  mainSurveyStore: MainSurveyStore;
  selectedBlock: SurveyBlocks.Block | null;
  setSelectedBlock: (block: SurveyBlocks.Block) => void;
  setSelectionArea: (area: string) => void;
  blockFormValuesStore: BlockFormValuesStore;
  positionInstructionsStore: PositionInstructionsStore;
  onBulkDeleted: (deletedIds: string[]) => void;
  onBulkSaved: () => void;
}
